export const FLOW_ID_COOKIE = "lea_flow_id";

export const SILENT_AUTH_FLOW_ID_COOKIE = "lea_silent_auth_flow_id";

export const CALLBACK_URL_COOKIES = [
	"__Secure-authjs.callback-url",
	"authjs.callback-url",
] as const;

export const AUTH_ROUND_TRIP_MAX_AGE_SECONDS = 30 * 60;

const RESERVED_FIRST_SEGMENTS = new Set([
	"api",
	"auth",
	"sign",
	"tink",
	"debug-error",
	"_next",
]);

const FIRST_SEGMENT_PATTERN = /^\/([^/?#]+)(?:\/|$)/;

/** Flow ids never contain a dot; `.well-known` probes and static files do. */
export const isFlowIdCandidate = (value: string | undefined): value is string =>
	value !== undefined &&
	value !== "" &&
	!value.includes(".") &&
	!RESERVED_FIRST_SEGMENTS.has(value);

export const extractFlowIdFromUrl = (
	url: string | undefined
): string | undefined => {
	if (!url) return undefined;

	try {
		const parsed = new URL(url, "http://localhost");
		const segment = FIRST_SEGMENT_PATTERN.exec(parsed.pathname)?.[1];
		if (isFlowIdCandidate(segment)) {
			return segment;
		}
		const fromQuery = parsed.searchParams.get("flowId")?.trim();
		return isFlowIdCandidate(fromQuery) ? fromQuery : undefined;
	} catch {
		return undefined;
	}
};

export interface FlowIdCarriers {
	/** Cookie lookup, i.e. `(name) => req.cookies.get(name)?.value`. */
	cookie: (name: string) => string | undefined;
	referer?: string;
	/** A `callbackUrl` query value, when the caller was given one. */
	callbackUrl?: string;
}

const decodeCookie = (value: string | undefined): string | undefined => {
	if (!value) return undefined;

	try {
		return decodeURIComponent(value);
	} catch {
		return value;
	}
};

export const resolveFlowIdFromCarriers = ({
	cookie,
	referer,
	callbackUrl,
}: FlowIdCarriers): string | undefined => {
	const fromCallbackUrl = extractFlowIdFromUrl(callbackUrl);
	if (fromCallbackUrl) return fromCallbackUrl;

	const flowIdCookie = decodeCookie(cookie(FLOW_ID_COOKIE));
	if (isFlowIdCandidate(flowIdCookie)) return flowIdCookie;

	const silentAuthFlowId = decodeCookie(cookie(SILENT_AUTH_FLOW_ID_COOKIE));
	if (isFlowIdCandidate(silentAuthFlowId)) return silentAuthFlowId;

	for (const name of CALLBACK_URL_COOKIES) {
		const flowId = extractFlowIdFromUrl(cookie(name));
		if (flowId) return flowId;
	}

	return extractFlowIdFromUrl(referer);
};
